import type { ScoringRules, UserAnswer, Question } from '@examflow/core';
import { JLPTLevel, JLPTSection, type JLPTScoreMapping, type JLPTResult } from './types';
import { getLevelDescription } from './levels';

/**
 * JLPT 分数与等级对照表
 * 基于官方 JLPT 评分标准 (总分 180，各部分 60 分)
 */
export const JLPT_SCORE_MAPPINGS: JLPTScoreMapping[] = [
  {
    level: JLPTLevel.N5,
    minScore: 80,
    maxScore: 89,
    passingScore: 80,
    description: '初级入门 - 能够理解基本的日语',
  },
  {
    level: JLPTLevel.N4,
    minScore: 90,
    maxScore: 94,
    passingScore: 90,
    description: '初级 - 能够理解日常日语的基本水平',
  },
  {
    level: JLPTLevel.N3,
    minScore: 95,
    maxScore: 99,
    passingScore: 95,
    description: '中级 - 能够在一定程度上理解日常生活中使用的日语',
  },
  {
    level: JLPTLevel.N2,
    minScore: 100,
    maxScore: 109,
    passingScore: 90,
    description: '中高级 - 能够理解日常场景中使用的日语',
  },
  {
    level: JLPTLevel.N1,
    minScore: 110,
    maxScore: 180,
    passingScore: 100,
    description: '高级 - 能够理解各种场景中使用的日语',
  },
];

/**
 * 根据分数获取 JLPT 等级
 */
export function getJLPTLevel(score: number): JLPTLevel {
  // 从高到低检查，优先匹配最高可达等级
  const sortedMappings = [...JLPT_SCORE_MAPPINGS].sort(
    (a, b) => b.minScore - a.minScore
  );
  for (const mapping of sortedMappings) {
    if (score >= mapping.minScore) {
      return mapping.level;
    }
  }
  return JLPTLevel.N5;
}

/**
 * 根据分数判断是否及格
 */
export function isJLPTPassed(score: number): boolean {
  return score >= 80;
}

/**
 * 获取等级详细信息
 */
export function getLevelInfo(level: JLPTLevel): JLPTScoreMapping | undefined {
  return JLPT_SCORE_MAPPINGS.find((m) => m.level === level);
}

/**
 * JLPT 评分规则
 * 总分 180，及格分 80 (N5 级别)
 */
export const jlptScoringRules: ScoringRules = {
  totalScore: 180,
  passingScore: 80,
  calculateScore: (answers: UserAnswer[], questions: Question[]): number => {
    if (questions.length === 0) return 0;

    const correctCount = answers.filter((a) => a.isCorrect).length;
    const accuracy = correctCount / questions.length;

    // 将正确率映射到 JLPT 分数范围 (0-180)
    const score = Math.round(accuracy * 180);

    return Math.min(180, Math.max(0, score));
  },
};

/**
 * 计算完整的 JLPT 结果
 */
export function calculateJLPTResult(
  answers: UserAnswer[],
  questions: Question[]
): JLPTResult {
  const score = jlptScoringRules.calculateScore(answers, questions);
  const level = getJLPTLevel(score);
  const passed = isJLPTPassed(score);
  const levelDesc = getLevelDescription(level);

  // 按部分计算分数（简化版：均匀分配）
  const sectionScores = {
    [JLPTSection.VOCABULARY]: 0,
    [JLPTSection.GRAMMAR_READING]: 0,
    [JLPTSection.LISTENING]: 0,
  };

  return {
    totalScore: score,
    level,
    sectionScores,
    passed,
    evaluation: levelDesc?.description || '继续努力！',
  };
}
